/**
 * DragDropHandler - Utility module for managing drag and drop operations
 * 
 * Provides drag and drop logic for the FileExplorer component including:
 * - Drag state management
 * - Single and multi-item drag support
 * - Drop target validation
 * - HTML5 drag and drop API integration
 * - macOS package bundle detection and auto-zipping
 * 
 * @module DragDropHandler
 * 
 * Requirements: 5.1, 5.2, 5.3
 * - 5.1: Support drag and drop to move files between folders
 * - 5.2: Allow dragging files to move them to a different folder
 * - 5.3: Support dropping files onto breadcrumb segments to move to parent folders
 */

import JSZip from 'jszip';

/**
 * Creates a new drag state object
 * @returns {Object} Initial drag state
 * @property {boolean} isDragging - Whether a drag operation is in progress
 * @property {Array<string>} draggedItems - Array of item keys being dragged
 * @property {string|null} dropTarget - Key of the current drop target
 * @property {string|null} dropTargetType - Type of drop target ('folder', 'breadcrumb', etc.)
 * @property {string|null} dragSource - Key of the source folder
 * 
 * @example
 * const dragState = createDragState();
 * // { isDragging: false, draggedItems: [], dropTarget: null, dropTargetType: null, dragSource: null }
 */
export function createDragState() {
  return {
    isDragging: false,
    draggedItems: [],
    dropTarget: null,
    dropTargetType: null,
    dragSource: null
  };
}

/**
 * Handles the start of a drag operation
 * If the dragged item is part of the current selection, all selected items are dragged.
 * Otherwise, only the clicked item is dragged.
 * 
 * @param {DragEvent} event - The drag start event
 * @param {string} itemKey - The key of the item being dragged
 * @param {Set<string>|Array<string>} selectedItems - Currently selected items
 * @param {string|null} sourceFolder - Key of the source folder (optional)
 * @returns {Object} Updated drag state
 * 
 * @example
 * // Dragging a selected item - drags all selected
 * const state = handleDragStart(event, 'file1.txt', new Set(['file1.txt', 'file2.txt']), 'folder1');
 * // state.draggedItems = ['file1.txt', 'file2.txt']
 * 
 * // Dragging an unselected item - drags only that item
 * const state = handleDragStart(event, 'file3.txt', new Set(['file1.txt', 'file2.txt']), 'folder1');
 * // state.draggedItems = ['file3.txt']
 */
export function handleDragStart(event, itemKey, selectedItems, sourceFolder = null) {
  // Validate inputs
  if (!event || !itemKey) {
    return createDragState();
  }

  // Convert selectedItems to array if it's a Set
  const selectedArray = selectedItems instanceof Set 
    ? Array.from(selectedItems) 
    : (Array.isArray(selectedItems) ? selectedItems : []);

  // Determine which items to drag
  let draggedItems;
  if (selectedArray.includes(itemKey)) {
    // If dragging a selected item, drag all selected items
    draggedItems = [...selectedArray];
  } else {
    // If dragging an unselected item, drag only that item
    draggedItems = [itemKey];
  }

  // Set drag data for HTML5 drag and drop API
  if (event.dataTransfer) {
    // Set the data as JSON string of item keys
    event.dataTransfer.setData('application/json', JSON.stringify({
      items: draggedItems,
      source: sourceFolder
    }));
    
    // Set a text fallback for compatibility
    event.dataTransfer.setData('text/plain', getDragPreviewText(draggedItems));
    
    // Set the drag effect
    event.dataTransfer.effectAllowed = 'move';
  }

  return {
    isDragging: true,
    draggedItems,
    dropTarget: null,
    dropTargetType: null,
    dragSource: sourceFolder
  };
}

/**
 * Handles drag over event on a potential drop target
 * Prevents default to allow dropping and updates the drop target state.
 * 
 * @param {DragEvent} event - The drag over event
 * @param {string} targetKey - The key of the potential drop target
 * @param {string} targetType - The type of target ('folder', 'breadcrumb', etc.)
 * @param {Object} currentDragState - Current drag state
 * @returns {Object} Updated drag state with drop target info
 * 
 * @example
 * const state = handleDragOver(event, 'folder2', 'folder', currentDragState);
 * // state.dropTarget = 'folder2', state.dropTargetType = 'folder'
 */
export function handleDragOver(event, targetKey, targetType, currentDragState = {}) {
  // Prevent default to allow drop
  if (event) {
    event.preventDefault();
    
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  // Return updated state with drop target
  return {
    ...createDragState(),
    ...currentDragState,
    isDragging: true,
    dropTarget: targetKey || null,
    dropTargetType: targetType || null
  };
}

/**
 * Handles drag leave event when leaving a drop target
 * Clears the drop target from the state.
 * 
 * @param {DragEvent} event - The drag leave event
 * @param {Object} currentDragState - Current drag state
 * @returns {Object} Updated drag state with cleared drop target
 * 
 * @example
 * const state = handleDragLeave(event, currentDragState);
 * // state.dropTarget = null, state.dropTargetType = null
 */
export function handleDragLeave(event, currentDragState = {}) {
  const safeState = currentDragState || {};
  return {
    ...createDragState(),
    ...safeState,
    isDragging: safeState.isDragging || false,
    draggedItems: safeState.draggedItems || [],
    dragSource: safeState.dragSource || null,
    dropTarget: null,
    dropTargetType: null
  };
}

/**
 * Handles the drop event on a target
 * Returns information about the drop operation for the caller to process.
 * 
 * @param {DragEvent} event - The drop event
 * @param {string} targetKey - The key of the drop target
 * @param {string} targetType - The type of target ('folder', 'breadcrumb', etc.)
 * @param {Object} dragState - Current drag state
 * @returns {Object|null} Drop info object or null if drop is invalid
 * @property {Array<string>} items - Array of item keys that were dropped
 * @property {string} target - Key of the drop target
 * @property {string} targetType - Type of the drop target
 * @property {string|null} source - Key of the source folder
 * 
 * @example
 * const dropInfo = handleDrop(event, 'folder2', 'folder', dragState);
 * // { items: ['file1.txt', 'file2.txt'], target: 'folder2', targetType: 'folder', source: 'folder1' }
 */
export function handleDrop(event, targetKey, targetType, dragState = {}) {
  // Prevent default browser behavior
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Get dragged items from drag state
  let draggedItems = dragState.draggedItems || [];
  let source = dragState.dragSource || null;

  // Try to get data from dataTransfer if available (for cross-component drops)
  if (event && event.dataTransfer) {
    try {
      const jsonData = event.dataTransfer.getData('application/json');
      if (jsonData) {
        const parsed = JSON.parse(jsonData);
        if (parsed.items && Array.isArray(parsed.items)) {
          draggedItems = parsed.items;
        }
        if (parsed.source !== undefined) {
          source = parsed.source;
        }
      }
    } catch (e) {
      // Use drag state data if parsing fails
    }
  }

  // Validate drop
  if (!draggedItems.length || !targetKey) {
    return null;
  }

  // Check if drop is valid
  if (!canDropOnTarget(draggedItems, targetKey, targetType)) {
    return null;
  }

  return {
    items: draggedItems,
    target: targetKey,
    targetType: targetType,
    source: source
  };
}

/**
 * Handles the end of a drag operation
 * Resets the drag state to initial values.
 * 
 * @returns {Object} Reset drag state
 * 
 * @example
 * const state = handleDragEnd();
 * // Returns initial drag state with isDragging: false
 */
export function handleDragEnd() {
  return createDragState();
}

/**
 * Checks if items can be dropped on a target
 * Validates that:
 * - A folder cannot be dropped on itself
 * - Items cannot be dropped on one of the dragged items
 * 
 * @param {Array<string>} draggedItems - Array of item keys being dragged
 * @param {string} targetKey - Key of the potential drop target
 * @param {string} targetType - Type of the target ('folder', 'breadcrumb', etc.)
 * @returns {boolean} True if the drop is valid
 * 
 * @example
 * canDropOnTarget(['folder1'], 'folder1', 'folder'); // false - can't drop on itself
 * canDropOnTarget(['file1.txt'], 'folder2', 'folder'); // true
 * canDropOnTarget(['file1.txt', 'folder1'], 'folder1', 'folder'); // false - target is in dragged items
 */
export function canDropOnTarget(draggedItems, targetKey, targetType) {
  // Validate inputs
  if (!draggedItems || !Array.isArray(draggedItems) || draggedItems.length === 0) {
    return false;
  }

  if (!targetKey) {
    return false;
  }

  // Can't drop on one of the dragged items
  if (draggedItems.includes(targetKey)) {
    return false;
  }

  // Additional validation could be added here for specific target types
  // For example, checking if target is a valid folder

  return true;
}

/**
 * Generates preview text for the drag operation
 * Shows item count or single item name.
 * 
 * @param {Array<string>|string} items - Item(s) being dragged
 * @returns {string} Preview text for the drag operation
 * 
 * @example
 * getDragPreviewText(['file1.txt']); // 'file1.txt'
 * getDragPreviewText(['file1.txt', 'file2.txt', 'file3.txt']); // '3 items'
 * getDragPreviewText('file1.txt'); // 'file1.txt'
 */
export function getDragPreviewText(items) {
  // Handle null/undefined
  if (!items) {
    return '';
  }

  // Convert to array if string
  const itemArray = Array.isArray(items) ? items : [items];

  // Handle empty array
  if (itemArray.length === 0) {
    return '';
  }

  // Single item - return the item name
  if (itemArray.length === 1) {
    return itemArray[0] || '';
  }

  // Multiple items - return count
  return `${itemArray.length} items`;
}

/**
 * Checks if a drag operation is currently in progress
 * 
 * @param {Object} dragState - Current drag state
 * @returns {boolean} True if dragging is in progress
 * 
 * @example
 * isDragging({ isDragging: true, draggedItems: ['file1.txt'] }); // true
 * isDragging({ isDragging: false, draggedItems: [] }); // false
 */
export function isDragging(dragState) {
  if (!dragState) {
    return false;
  }
  return dragState.isDragging === true;
}

/**
 * Gets the current drop target from drag state
 * 
 * @param {Object} dragState - Current drag state
 * @returns {Object|null} Drop target info or null
 * @property {string} key - Key of the drop target
 * @property {string} type - Type of the drop target
 * 
 * @example
 * getDropTarget({ dropTarget: 'folder1', dropTargetType: 'folder' });
 * // { key: 'folder1', type: 'folder' }
 */
export function getDropTarget(dragState) {
  if (!dragState || !dragState.dropTarget) {
    return null;
  }

  return {
    key: dragState.dropTarget,
    type: dragState.dropTargetType
  };
}

/**
 * Checks if a specific element is the current drop target
 * 
 * @param {string} itemKey - Key of the item to check
 * @param {Object} dragState - Current drag state
 * @returns {boolean} True if the item is the current drop target
 * 
 * @example
 * isDropTarget('folder1', { dropTarget: 'folder1' }); // true
 * isDropTarget('folder2', { dropTarget: 'folder1' }); // false
 */
export function isDropTarget(itemKey, dragState) {
  if (!itemKey || !dragState) {
    return false;
  }

  return dragState.dropTarget === itemKey;
}

/**
 * Checks if an item is being dragged
 * 
 * @param {string} itemKey - Key of the item to check
 * @param {Object} dragState - Current drag state
 * @returns {boolean} True if the item is being dragged
 * 
 * @example
 * isItemBeingDragged('file1.txt', { draggedItems: ['file1.txt', 'file2.txt'] }); // true
 * isItemBeingDragged('file3.txt', { draggedItems: ['file1.txt', 'file2.txt'] }); // false
 */
export function isItemBeingDragged(itemKey, dragState) {
  if (!itemKey || !dragState || !dragState.draggedItems) {
    return false;
  }

  return dragState.draggedItems.includes(itemKey);
}

// ============================================================================
// External File Drag and Drop Support
// Requirements: 6.1, 6.2
// - 6.1: Display a visual drop zone indicator when external files are dragged over
// - 6.2: Upload external files to the current folder when dropped
// ============================================================================

/**
 * Creates initial state for external drag operations
 * @returns {Object} Initial external drag state
 * @property {boolean} isExternalDrag - Whether an external drag is in progress
 * @property {boolean} showDropZone - Whether to show the drop zone indicator
 * 
 * @example
 * const state = createExternalDragState();
 * // { isExternalDrag: false, showDropZone: false }
 */
export function createExternalDragState() {
  return {
    isExternalDrag: false,
    showDropZone: false
  };
}

/**
 * Detects if a drag event contains external files (from the OS file system)
 * External drags have files in dataTransfer.types but no internal drag data.
 * 
 * Requirement 6.1: Detect external file drag (from OS)
 * 
 * @param {DragEvent} event - The drag event to check
 * @returns {boolean} True if the drag contains external files
 * 
 * @example
 * // External file drag from OS
 * isExternalFileDrag(event); // true when dragging from Finder/Explorer
 * 
 * // Internal drag within the app
 * isExternalFileDrag(event); // false when dragging files within FileExplorer
 */
export function isExternalFileDrag(event) {
  if (!event || !event.dataTransfer) {
    return false;
  }

  const types = event.dataTransfer.types;
  
  // Check if types is available and contains 'Files'
  if (!types) {
    return false;
  }

  // Convert types to array if it's a DOMStringList
  const typesArray = Array.isArray(types) ? types : Array.from(types);
  
  // External file drags have 'Files' in types
  const hasFiles = typesArray.includes('Files');
  
  // Internal drags have our custom application/json type
  const hasInternalData = typesArray.includes('application/json');
  
  // It's an external drag if it has Files but no internal data
  return hasFiles && !hasInternalData;
}

/**
 * Handles drag enter event for external file drops
 * Shows the drop zone indicator when external files are dragged over.
 * 
 * Requirement 6.1: Show visual drop zone indicator
 * 
 * @param {DragEvent} event - The drag enter event
 * @param {Object} currentState - Current external drag state
 * @returns {Object} Updated external drag state
 * 
 * @example
 * const state = handleExternalDragEnter(event, currentState);
 * // { isExternalDrag: true, showDropZone: true }
 */
export function handleExternalDragEnter(event, currentState = {}) {
  if (!event) {
    return createExternalDragState();
  }

  // Prevent default to allow drop
  event.preventDefault();
  
  // Check if this is an external file drag
  if (isExternalFileDrag(event)) {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    
    return {
      ...currentState,
      isExternalDrag: true,
      showDropZone: true
    };
  }
  
  return currentState || createExternalDragState();
}

/**
 * Handles drag over event for external file drops
 * Maintains the drop zone indicator while files are being dragged over.
 * 
 * Requirement 6.1: Show visual drop zone indicator
 * 
 * @param {DragEvent} event - The drag over event
 * @param {Object} currentState - Current external drag state
 * @returns {Object} Updated external drag state
 * 
 * @example
 * const state = handleExternalDragOver(event, currentState);
 */
export function handleExternalDragOver(event, currentState = {}) {
  if (!event) {
    return currentState || createExternalDragState();
  }

  // Prevent default to allow drop
  event.preventDefault();
  
  // Check if this is an external file drag
  if (isExternalFileDrag(event)) {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    
    return {
      ...currentState,
      isExternalDrag: true,
      showDropZone: true
    };
  }
  
  return currentState || createExternalDragState();
}

/**
 * Handles drag leave event for external file drops
 * Hides the drop zone indicator when files are dragged away.
 * Uses a counter-based approach to handle nested elements.
 * 
 * @param {DragEvent} event - The drag leave event
 * @param {Object} currentState - Current external drag state
 * @param {HTMLElement} containerElement - The container element to check against
 * @returns {Object} Updated external drag state
 * 
 * @example
 * const state = handleExternalDragLeave(event, currentState, containerRef.current);
 */
export function handleExternalDragLeave(event, currentState = {}, containerElement = null) {
  if (!event) {
    return currentState || createExternalDragState();
  }

  // Check if we're leaving the container entirely
  // relatedTarget is the element we're entering
  const relatedTarget = event.relatedTarget;
  
  // If relatedTarget is null or outside the container, hide the drop zone
  if (containerElement) {
    if (!relatedTarget || !containerElement.contains(relatedTarget)) {
      return {
        ...currentState,
        isExternalDrag: false,
        showDropZone: false
      };
    }
    // Still inside the container, keep showing drop zone
    return currentState;
  }
  
  // Without container reference, just hide on any leave
  return {
    ...currentState,
    isExternalDrag: false,
    showDropZone: false
  };
}

/**
 * Handles drop event for external files
 * Extracts the dropped files and returns them for upload processing.
 * 
 * Requirement 6.2: Handle file drop to trigger upload
 * 
 * @param {DragEvent} event - The drop event
 * @returns {Object|null} Drop result with files array, or null if no files
 * @property {FileList|File[]} files - The dropped files
 * @property {number} fileCount - Number of files dropped
 * 
 * @example
 * const result = handleExternalDrop(event);
 * // { files: FileList, fileCount: 3 }
 */
export function handleExternalDrop(event) {
  if (!event) {
    return null;
  }

  // Prevent default browser behavior (opening the file)
  event.preventDefault();
  event.stopPropagation();

  // Get the dropped files
  const files = event.dataTransfer?.files;
  
  if (!files || files.length === 0) {
    return null;
  }

  return {
    files: files,
    fileCount: files.length
  };
}

/**
 * Resets the external drag state
 * Called after a drop or when drag ends.
 * 
 * @returns {Object} Reset external drag state
 * 
 * @example
 * const state = resetExternalDragState();
 * // { isExternalDrag: false, showDropZone: false }
 */
export function resetExternalDragState() {
  return createExternalDragState();
}

/**
 * Checks if the drop zone should be shown
 * 
 * @param {Object} externalDragState - Current external drag state
 * @returns {boolean} True if drop zone should be visible
 * 
 * @example
 * shouldShowDropZone({ isExternalDrag: true, showDropZone: true }); // true
 * shouldShowDropZone({ isExternalDrag: false, showDropZone: false }); // false
 */
export function shouldShowDropZone(externalDragState) {
  if (!externalDragState) {
    return false;
  }
  
  return externalDragState.showDropZone === true;
}

// ============================================================================
// Folder Drop Support for Recursive Upload
// Requirements: 6.4, 6.5
// - 6.4: Support dropping folders to upload their entire contents recursively
// - 6.5: If an upload fails, display an error and allow retry
// ============================================================================

/**
 * Checks if a drop event contains folders using the DataTransferItem API.
 * Uses webkitGetAsEntry() to detect folder entries.
 * 
 * Requirement 6.4: Detect folder in dropped items
 * 
 * @param {DragEvent} event - The drop event to check
 * @returns {boolean} True if the drop contains at least one folder
 * 
 * @example
 * // Drop event with a folder
 * containsFolder(event); // true
 * 
 * // Drop event with only files
 * containsFolder(event); // false
 */
export function containsFolder(event) {
  if (!event || !event.dataTransfer || !event.dataTransfer.items) {
    return false;
  }

  const items = event.dataTransfer.items;
  
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    
    // Use webkitGetAsEntry to check if item is a directory
    if (item.webkitGetAsEntry) {
      const entry = item.webkitGetAsEntry();
      if (entry && entry.isDirectory) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Gets file system entries from a drop event.
 * Returns an array of FileSystemEntry objects for both files and folders.
 * 
 * Requirement 6.4: Get entries for recursive folder reading
 * 
 * @param {DragEvent} event - The drop event
 * @returns {Array<FileSystemEntry>} Array of file system entries
 * 
 * @example
 * const entries = getDroppedEntries(event);
 * // [FileSystemFileEntry, FileSystemDirectoryEntry, ...]
 */
export function getDroppedEntries(event) {
  if (!event || !event.dataTransfer || !event.dataTransfer.items) {
    return [];
  }

  const entries = [];
  const items = event.dataTransfer.items;
  
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    
    if (item.webkitGetAsEntry) {
      const entry = item.webkitGetAsEntry();
      if (entry) {
        entries.push(entry);
      }
    }
  }
  
  return entries;
}

/**
 * Reads all entries from a FileSystemDirectoryEntry recursively.
 * Returns a flat array of all files with their relative paths.
 * 
 * Requirement 6.4: Recursively read folder contents
 * 
 * @param {FileSystemDirectoryEntry} directoryEntry - The directory entry to read
 * @param {string} basePath - The base path for relative paths (default: '')
 * @returns {Promise<Array<{file: File, relativePath: string}>>} Array of files with paths
 * 
 * @example
 * const files = await readDirectoryRecursive(folderEntry, 'myFolder');
 * // [{ file: File, relativePath: 'myFolder/subdir/file.txt' }, ...]
 */
export async function readDirectoryRecursive(directoryEntry, basePath = '') {
  if (!directoryEntry || !directoryEntry.isDirectory) {
    return [];
  }

  const results = [];
  const currentPath = basePath ? `${basePath}/${directoryEntry.name}` : directoryEntry.name;
  
  try {
    const entries = await readDirectoryEntries(directoryEntry);
    
    for (const entry of entries) {
      if (entry.isFile) {
        // Read the file from the entry
        const file = await getFileFromEntry(entry);
        if (file) {
          results.push({
            file,
            relativePath: `${currentPath}/${entry.name}`
          });
        }
      } else if (entry.isDirectory) {
        // Recursively read subdirectory
        const subResults = await readDirectoryRecursive(entry, currentPath);
        results.push(...subResults);
      }
    }
  } catch (error) {
    console.error(`Error reading directory ${currentPath}:`, error);
    // Return what we have so far, don't fail completely
  }
  
  return results;
}

/**
 * Reads all entries from a directory using the FileSystemDirectoryReader.
 * Handles the async reader API and batched results.
 * 
 * @param {FileSystemDirectoryEntry} directoryEntry - The directory to read
 * @returns {Promise<Array<FileSystemEntry>>} Array of entries in the directory
 */
export function readDirectoryEntries(directoryEntry) {
  return new Promise((resolve, reject) => {
    if (!directoryEntry || !directoryEntry.createReader) {
      resolve([]);
      return;
    }

    const reader = directoryEntry.createReader();
    const allEntries = [];
    
    // readEntries returns results in batches, so we need to call it repeatedly
    const readBatch = () => {
      reader.readEntries(
        (entries) => {
          if (entries.length === 0) {
            // No more entries, we're done
            resolve(allEntries);
          } else {
            // Add entries and read next batch
            allEntries.push(...entries);
            readBatch();
          }
        },
        (error) => {
          // If we have some entries, return them; otherwise reject
          if (allEntries.length > 0) {
            resolve(allEntries);
          } else {
            reject(error);
          }
        }
      );
    };
    
    readBatch();
  });
}

/**
 * Gets a File object from a FileSystemFileEntry.
 * 
 * @param {FileSystemFileEntry} fileEntry - The file entry
 * @returns {Promise<File|null>} The File object or null on error
 */
export function getFileFromEntry(fileEntry) {
  return new Promise((resolve) => {
    if (!fileEntry || !fileEntry.file) {
      resolve(null);
      return;
    }

    fileEntry.file(
      (file) => resolve(file),
      (error) => {
        console.error(`Error getting file from entry:`, error);
        resolve(null);
      }
    );
  });
}

// ============================================================================
// macOS Package Bundle Detection and Auto-Zipping
// Handles .logicx, .band, .app, and other macOS package bundles
// ============================================================================

/**
 * List of macOS package bundle extensions.
 * These appear as single files in Finder but are actually folders.
 * When dropped in a browser, macOS exposes their internal structure.
 */
const MACOS_PACKAGE_EXTENSIONS = [
  '.logicx',      // Logic Pro X projects
  '.band',        // GarageBand projects
  '.app',         // macOS applications
  '.bundle',      // Generic bundles
  '.framework',   // Frameworks
  '.plugin',      // Plugins (AU, VST, etc.)
  '.component',   // Audio Unit components
  '.kext',        // Kernel extensions
  '.pkg',         // Installer packages
  '.mpkg',        // Meta packages
  '.prefPane',    // Preference panes
  '.qlgenerator', // Quick Look generators
  '.saver',       // Screen savers
  '.wdgt',        // Dashboard widgets
  '.xcodeproj',   // Xcode projects
  '.xcworkspace', // Xcode workspaces
  '.playground',  // Swift playgrounds
  '.photoslibrary', // Photos libraries
  '.fcpbundle',   // Final Cut Pro bundles
  '.fcpproject',  // Final Cut Pro projects (older)
  '.motion',      // Motion projects
  '.motn',        // Motion templates
  '.key',         // Keynote files (can be packages)
  '.pages',       // Pages files (can be packages)
  '.numbers',     // Numbers files (can be packages)
];

/**
 * Checks if a directory entry is a macOS package bundle.
 * Package bundles have specific extensions and appear as folders when dropped.
 * 
 * @param {FileSystemDirectoryEntry} entry - The directory entry to check
 * @returns {boolean} True if the entry is a package bundle
 * 
 * @example
 * isPackageBundle(entry); // true for 'MySong.logicx'
 * isPackageBundle(entry); // false for 'MyFolder'
 */
export function isPackageBundle(entry) {
  if (!entry || !entry.name) {
    return false;
  }
  
  const name = entry.name.toLowerCase();
  return MACOS_PACKAGE_EXTENSIONS.some(ext => name.endsWith(ext));
}

/**
 * Gets the package bundle extension from a name.
 * 
 * @param {string} name - The file/folder name
 * @returns {string|null} The package extension or null if not a package
 */
export function getPackageExtension(name) {
  if (!name) return null;
  
  const nameLower = name.toLowerCase();
  return MACOS_PACKAGE_EXTENSIONS.find(ext => nameLower.endsWith(ext)) || null;
}

/**
 * Zips a directory entry (package bundle) into a single File object.
 * Recursively reads all contents and creates a zip archive.
 * 
 * @param {FileSystemDirectoryEntry} directoryEntry - The directory to zip
 * @param {Function} onProgress - Optional progress callback (0-100)
 * @returns {Promise<{file: File, originalName: string}|null>} The zipped file or null on error
 * 
 * @example
 * const result = await zipDirectoryEntry(logicxEntry);
 * // { file: File (MySong.logicx.zip), originalName: 'MySong.logicx' }
 */
export async function zipDirectoryEntry(directoryEntry, onProgress = null) {
  if (!directoryEntry || !directoryEntry.isDirectory) {
    return null;
  }

  const zip = new JSZip();
  const bundleName = directoryEntry.name;
  
  try {
    // Recursively add all files to the zip
    await addDirectoryToZip(zip, directoryEntry, bundleName, onProgress);
    
    // Generate the zip file
    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    }, (metadata) => {
      if (onProgress) {
        onProgress(Math.round(metadata.percent));
      }
    });
    
    // Create a File object from the blob
    const zipFileName = `${bundleName}.zip`;
    const zipFile = new File([zipBlob], zipFileName, { type: 'application/zip' });
    
    return {
      file: zipFile,
      originalName: bundleName
    };
  } catch (error) {
    console.error(`Error zipping package bundle "${bundleName}":`, error);
    return null;
  }
}

/**
 * Recursively adds a directory's contents to a JSZip instance.
 * 
 * @param {JSZip} zip - The JSZip instance
 * @param {FileSystemDirectoryEntry} directoryEntry - The directory to add
 * @param {string} zipPath - The path within the zip file
 * @param {Function} onProgress - Optional progress callback
 */
async function addDirectoryToZip(zip, directoryEntry, zipPath, onProgress = null) {
  const entries = await readDirectoryEntries(directoryEntry);
  
  for (const entry of entries) {
    const entryPath = `${zipPath}/${entry.name}`;
    
    if (entry.isFile) {
      const file = await getFileFromEntry(entry);
      if (file) {
        // Read file as array buffer and add to zip
        const arrayBuffer = await file.arrayBuffer();
        zip.file(entryPath, arrayBuffer);
      }
    } else if (entry.isDirectory) {
      // Recursively add subdirectory
      await addDirectoryToZip(zip, entry, entryPath, onProgress);
    }
  }
}

/**
 * Processes dropped items and extracts all files, including from folders recursively.
 * Detects macOS package bundles (.logicx, .band, etc.) and auto-zips them.
 * Returns a flat array of files with their relative paths for upload.
 * 
 * Requirements: 6.4, 6.5
 * - 6.4: Support dropping folders to upload their entire contents recursively
 * - 6.5: Handle errors gracefully
 * - Auto-zip macOS package bundles for seamless upload
 * 
 * @param {DragEvent} event - The drop event
 * @param {Function} onZipProgress - Optional callback for zip progress (bundleName, progress)
 * @returns {Promise<{files: Array<{file: File, relativePath: string}>, hasFolder: boolean, hasPackageBundle: boolean, errors: Array<string>}>}
 * 
 * @example
 * const result = await processDroppedItems(event);
 * // {
 * //   files: [{ file: File, relativePath: 'folder/file.txt' }, ...],
 * //   hasFolder: true,
 * //   hasPackageBundle: false,
 * //   errors: []
 * // }
 */
export async function processDroppedItems(event, onZipProgress = null) {
  if (!event || !event.dataTransfer) {
    return { files: [], hasFolder: false, hasPackageBundle: false, errors: [] };
  }

  const result = {
    files: [],
    hasFolder: false,
    hasPackageBundle: false,
    errors: []
  };

  // Check if we have the items API (for folder support)
  if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
    const items = event.dataTransfer.items;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Try to get entry for folder detection
      if (item.webkitGetAsEntry) {
        const entry = item.webkitGetAsEntry();
        
        if (entry) {
          if (entry.isDirectory) {
            // Check if this is a macOS package bundle
            if (isPackageBundle(entry)) {
              result.hasPackageBundle = true;
              
              try {
                // Zip the package bundle instead of recursing into it
                console.log(`Detected package bundle: ${entry.name}, zipping...`);
                const zipResult = await zipDirectoryEntry(entry, (progress) => {
                  if (onZipProgress) {
                    onZipProgress(entry.name, progress);
                  }
                });
                
                if (zipResult) {
                  result.files.push({
                    file: zipResult.file,
                    relativePath: zipResult.file.name,
                    isZippedBundle: true,
                    originalBundleName: zipResult.originalName
                  });
                  console.log(`Successfully zipped ${entry.name} -> ${zipResult.file.name} (${(zipResult.file.size / 1024 / 1024).toFixed(2)} MB)`);
                } else {
                  result.errors.push(`Failed to zip package bundle "${entry.name}"`);
                }
              } catch (error) {
                result.errors.push(`Failed to process package bundle "${entry.name}": ${error.message}`);
              }
            } else {
              // Regular folder - recursively read contents
              result.hasFolder = true;
              
              try {
                const folderFiles = await readDirectoryRecursive(entry, '');
                result.files.push(...folderFiles);
              } catch (error) {
                result.errors.push(`Failed to read folder "${entry.name}": ${error.message}`);
              }
            }
          } else if (entry.isFile) {
            // Get file from entry
            try {
              const file = await getFileFromEntry(entry);
              if (file) {
                result.files.push({
                  file,
                  relativePath: file.name
                });
              }
            } catch (error) {
              result.errors.push(`Failed to read file "${entry.name}": ${error.message}`);
            }
          }
        }
      } else if (item.kind === 'file') {
        // Fallback for browsers without webkitGetAsEntry
        const file = item.getAsFile();
        if (file) {
          result.files.push({
            file,
            relativePath: file.name
          });
        }
      }
    }
  } else if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
    // Fallback to files API (no folder support)
    const files = event.dataTransfer.files;
    for (let i = 0; i < files.length; i++) {
      result.files.push({
        file: files[i],
        relativePath: files[i].name
      });
    }
  }

  return result;
}

/**
 * Creates a folder upload progress state object.
 * Used to track progress of recursive folder uploads.
 * 
 * Requirement 6.4: Show progress for the entire folder upload operation
 * 
 * @returns {Object} Initial folder upload progress state
 */
export function createFolderUploadProgress() {
  return {
    isUploading: false,
    totalFiles: 0,
    completedFiles: 0,
    currentFile: null,
    currentFileProgress: 0,
    folderName: null,
    errors: [],
    retryQueue: []
  };
}

/**
 * Calculates overall progress percentage for folder upload.
 * 
 * @param {Object} progress - The folder upload progress state
 * @returns {number} Progress percentage (0-100)
 */
export function calculateFolderUploadProgress(progress) {
  if (!progress || progress.totalFiles === 0) {
    return 0;
  }

  // Each file contributes equally to overall progress
  // Current file progress is factored in for smoother updates
  const completedProgress = (progress.completedFiles / progress.totalFiles) * 100;
  const currentFileContribution = (progress.currentFileProgress / 100) * (100 / progress.totalFiles);
  
  return Math.min(Math.round(completedProgress + currentFileContribution), 100);
}
